import React from 'react';
import { CheckCircle, X } from 'lucide-react';

export interface SessionSummaryData {
  sessionName: string;
  duration: string;

  // Transcripts
  totalTranscripts: number;
  salespersonTranscripts: number;
  customerTranscripts: number;

  // Landmines
  totalLandmines: number;
  salespersonLandmines: number;
  customerLandmines: number;

  // Competitors
  totalCompetitors: number;
  salespersonCompetitors: number;
  customerCompetitors: number;

  // Questions by category
  objections: number;
  faqs: number;
  newInquiries: number;

}

interface SessionSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClearSession: () => void;
  summary: SessionSummaryData;
}

const SessionSummaryModal: React.FC<SessionSummaryModalProps> = ({
  isOpen,
  onClose,
  onClearSession,
  summary,
}) => {
  if (!isOpen) return null;

  const StatRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );

  const TotalItem: React.FC<{ value: number; label: string }> = ({ value, label }) => (
    <div className="flex items-center gap-1">
      <span className="text-xs font-semibold text-gray-600">{value}</span>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[420px] overflow-hidden">
        {/* Header */}
        <div className="bg-green-500 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Session Complete
              </h2>
              <div className="flex items-center text-sm text-white/80 mt-1">
                <span>{summary.sessionName}</span>
                <span className="mx-2 text-white/60">|</span>
                <span>{summary.duration}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body: Two Columns */}
        <div className="px-5 py-4">
          <div className="flex gap-4">
            {/* Left Column: Salesperson */}
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Salesperson</h3>
              <div className="space-y-1">
                <StatRow label="Transcripts" value={summary.salespersonTranscripts} />
                <StatRow label="Landmine Triggers" value={summary.salespersonLandmines} />
                <StatRow label="Competitor Mentions" value={summary.salespersonCompetitors} />
              </div>
            </div>

            {/* Divider */}
            <div className="w-px bg-gray-200" />

            {/* Right Column: Customer */}
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Customer</h3>
              <div className="space-y-1">
                <StatRow label="Transcripts" value={summary.customerTranscripts} />
                <StatRow label="Landmine Triggers" value={summary.customerLandmines} />
                <StatRow label="Competitor Mentions" value={summary.customerCompetitors} />
                <StatRow label="Objections" value={summary.objections} />
                <StatRow label="FAQs" value={summary.faqs} />
                <StatRow label="New Inquiries" value={summary.newInquiries} />
              </div>
            </div>
          </div>
        </div>

        {/* Totals Row */}
        <div className="border-t border-gray-200 px-5 py-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase">Totals</span>
            <div className="flex items-center gap-4">
              <TotalItem value={summary.totalTranscripts} label="transcripts" />
              <TotalItem value={summary.totalLandmines} label="landmines" />
              <TotalItem value={summary.totalCompetitors} label="competitors" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onClearSession}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
          >
            Clear Session Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionSummaryModal;
